import { useContext, useEffect, useMemo } from 'react';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../constants/constants';
import { SBOMContext } from './NRSBOMDataShare';
import { OrgLevelSettingsContext } from '../NRSettings/NRGeneralSettings/NROrgLevelSettingsContextWrapper';
import { getSbomColumns } from './NRSBOMColumns.utils';
import { getSbomNestedColumns } from './NRSBOMNestedColumns.utils';

/**
 * Keeps `SBOMContext.enabledIdentificationMethods` in sync with the organization settings.
 * Mount it once above both the flat and the grouped SBOM tables (the flat table is not mounted when grouping).
 */
export const useSbomEnabledMethods = () => {
  const { setEnabledIdentificationMethods } = useContext(SBOMContext);
  const { orgLevelSettings, setRefetch: setOrgLevelSettingsRefetch } = useContext(OrgLevelSettingsContext);
  const { value: isComponentIdentifiedViaEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_IDENTIFIED_VIA, false);

  useEffect(() => {
    if (isComponentIdentifiedViaEnabled) {
      setOrgLevelSettingsRefetch(true);
    }
  }, [isComponentIdentifiedViaEnabled, setOrgLevelSettingsRefetch]);

  useEffect(() => {
    if (!orgLevelSettings) return;
    setEnabledIdentificationMethods({
      symbolIndex: orgLevelSettings.symbolIndexEnabled?.enabled ?? true,
      kernelModule: orgLevelSettings.kernelModuleEnabled?.enabled ?? true,
      curatedHash: orgLevelSettings.curatedHashEnabled?.enabled ?? true,
      legacyHash: orgLevelSettings.legacyHashEnabled?.enabled ?? true,
      packageManifest: orgLevelSettings.packageManifestEnabled?.enabled ?? true,
      signature: orgLevelSettings.signatureEnabled?.enabled ?? true,
      peMetaData: orgLevelSettings.peMetaDataEnabled?.enabled ?? true,
      libraryVersion: orgLevelSettings.libraryVersionEnabled?.enabled ?? true,
      libraryName: orgLevelSettings.libraryNameEnabled?.enabled ?? true,
      binaryFingerprint: orgLevelSettings.binaryFingerprintEnabled?.enabled ?? true
    });
  }, [orgLevelSettings, setEnabledIdentificationMethods]);
};

/**
 * SBOM columns after applying the feature flags and the organization's enabled verification / identification methods.
 * Without `groupedBy` it returns the columns of the flat table; with it, the columns of the nested table of a grouped row
 * (the grouping column removed).
 */
export const useSbomColumns = ({ columns, groupedBy } = {}) => {
  const { enabledVerificationMethods, enabledIdentificationMethods } = useContext(SBOMContext);
  const { value: isIdentifiedViaEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_IDENTIFIED_VIA, false);
  const { value: isConfidenceEnabled } = useFlag(FEATURE_FLAGS.COMPONENT_CONFIDENCE, false);
  const { value: isAiRelatedDependenciesEnabled } = useFlag(FEATURE_FLAGS.AI_RELATED_DEPENDENCIES, false);

  return useMemo(() => {
    const options = {
      columns,
      isIdentifiedViaEnabled,
      isConfidenceEnabled,
      isAiRelatedDependenciesEnabled,
      enabledVerificationMethods,
      enabledIdentificationMethods
    };
    return groupedBy ? getSbomNestedColumns({ ...options, groupedBy }) : getSbomColumns(options);
  }, [
    columns,
    groupedBy,
    isIdentifiedViaEnabled,
    isConfidenceEnabled,
    isAiRelatedDependenciesEnabled,
    enabledVerificationMethods,
    enabledIdentificationMethods
  ]);
};
